import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { PasswordResetService } from '../password-reset.service';
import { environment } from 'src/environments/environment';
import {
  FirebasePasswordPolicyService,
  createInitialPasswordStrengthState,
} from '../password-policy.service';

@Component({
  selector: 'app-auth-actions',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButton,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.scss'],
})
export class ActionsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly reset = inject(PasswordResetService);
  private readonly policy = inject(FirebasePasswordPolicyService);
  private readonly destroyRef = inject(DestroyRef);
  private code = '';
  private version = 0;
  private redirectTimer?: ReturnType<typeof setTimeout>;
  private validationTimer?: ReturnType<typeof setTimeout>;
  private continueUrl: string | null = null;

  readonly password = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  readonly state = signal<'loading' | 'ready' | 'error' | 'success'>('loading');
  readonly email = signal('');
  readonly mode = signal<'resetPassword' | 'verifyEmail'>('resetPassword');
  readonly error = signal('');
  readonly validating = signal(false);
  readonly saving = signal(false);
  readonly showPassword = signal(false);
  readonly passwordStrength = signal(createInitialPasswordStrengthState());
  readonly validation = computed(() => this.passwordStrength().policy);
  readonly countdown = signal(5);
  readonly redirecting = signal(false);

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      void this.openLink(params);
    });
    this.password.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.version++;
      clearTimeout(this.validationTimer);
      this.error.set('');
      this.passwordStrength.set(
        createInitialPasswordStrengthState(this.passwordStrength().minCheckLength),
      );
      this.validating.set(!!this.password.value && this.state() === 'ready');
      if (this.validating()) {
        this.validationTimer = setTimeout(() => {
          void this.checkPassword();
        }, this.policy.debounceMs);
      }
    });
    this.destroyRef.onDestroy(() => {
      this.version++;
      clearTimeout(this.redirectTimer);
      clearTimeout(this.validationTimer);
      this.policy.clearCache();
    });
  }

  private async openLink(params: ParamMap) {
    const version = ++this.version;
    clearTimeout(this.redirectTimer);
    clearTimeout(this.validationTimer);
    this.redirecting.set(false);
    this.validating.set(false);
    this.state.set('loading');
    this.error.set('');
    this.email.set('');
    this.passwordStrength.set(createInitialPasswordStrengthState());
    this.password.reset('', { emitEvent: false });
    this.continueUrl = null;
    this.mode.set(params.get('mode') === 'verifyEmail' ? 'verifyEmail' : 'resetPassword');
    const continueUrl = params.get('continueUrl');
    if (
      !['resetPassword', 'verifyEmail'].includes(params.get('mode')) ||
      !params.get('oobCode')?.trim() ||
      params.getAll('mode').length !== 1 ||
      params.getAll('oobCode').length !== 1 ||
      params.getAll('continueUrl').length > 1 ||
      (continueUrl !== null && !this.isAllowedContinueUrl(continueUrl))
    ) {
      this.state.set('error');
      this.error.set(
        this.mode() === 'verifyEmail'
          ? 'Deze verificatielink is ongeldig. Vraag een nieuwe verificatiemail aan.'
          : 'Deze resetlink is ongeldig. Vraag een nieuwe resetlink aan.',
      );
      return;
    }
    this.code = params.get('oobCode');
    this.continueUrl = continueUrl;
    try {
      if (this.mode() === 'verifyEmail') {
        const email = await this.reset.verifyEmail(this.code);
        if (version !== this.version) return;
        this.email.set(email);
        this.state.set('success');
        this.startRedirect();
        return;
      }
      const email = await this.reset.verifyCode(this.code);
      const initialStrength = await this.policy.getInitialState();
      if (version !== this.version) return;
      this.email.set(email);
      this.passwordStrength.set(initialStrength);
      this.state.set('ready');
    } catch (error) {
      if (version !== this.version) return;
      this.state.set('error');
      this.error.set(this.errorMessage(error));
    }
  }

  private isAllowedContinueUrl(value: string): boolean {
    if (!value.trim()) return false;
    try {
      const url = new URL(value, window.location.origin);
      const userLoginUrl = new URL('/auth/login', environment.userUrl);
      return (
        ((url.origin === window.location.origin && url.pathname === '/login') ||
          (url.origin === userLoginUrl.origin && url.pathname === userLoginUrl.pathname)) &&
        !url.search &&
        !url.hash &&
        !url.username &&
        !url.password
      );
    } catch {
      return false;
    }
  }

  async checkPassword() {
    clearTimeout(this.validationTimer);
    const version = ++this.version;
    this.passwordStrength.set(
      createInitialPasswordStrengthState(this.passwordStrength().minCheckLength),
    );
    this.error.set('');
    this.validating.set(false);
    if (!this.password.value || this.state() !== 'ready') return;
    this.validating.set(true);
    try {
      const result = await this.policy.evaluatePassword(this.password.value);
      if (version === this.version) this.passwordStrength.set(result);
    } catch (error) {
      if (version === this.version) {
        this.error.set(this.policy.getPasswordValidationErrorMessage(error));
      }
    } finally {
      if (version === this.version) this.validating.set(false);
    }
  }

  requirements() {
    const result = this.validation();
    const options = result?.passwordPolicy.customStrengthOptions;
    const requirements: { met: boolean | null | undefined; text: string }[] = result
      ? [
          {
            met: result.meetsMinPasswordLength,
            text: `Minimaal ${options.minPasswordLength} tekens`,
          },
          { met: result.containsLowercaseLetter, text: 'Een kleine letter' },
          { met: result.containsUppercaseLetter, text: 'Een hoofdletter' },
          { met: result.containsNumericCharacter, text: 'Een cijfer' },
          { met: result.containsNonAlphanumericCharacter, text: 'Een speciaal teken' },
        ]
      : [];
    const strength = this.passwordStrength();
    if (strength.guessability !== null)
      requirements.push({
        met: strength.guessability,
        text: 'Voldoende onvoorspelbaar wachtwoord',
      });
    if (strength.compromised !== null)
      requirements.push({
        met: !strength.compromised,
        text: 'Niet gevonden in bekende datalekken',
      });
    return requirements.filter((requirement) => requirement.met !== undefined);
  }

  async save() {
    if (
      this.state() !== 'ready' ||
      this.saving() ||
      this.validating() ||
      !this.passwordStrength().isStrong ||
      !this.password.valid
    )
      return;
    this.saving.set(true);
    this.error.set('');
    const password = this.password.value;
    const version = this.version;
    let confirming = false;
    try {
      const result = await this.policy.evaluatePassword(password, true);
      if (version !== this.version) return;
      this.passwordStrength.set(result);
      if (!result.isStrong) {
        if (result.compromised)
          this.error.set(
            'Dit wachtwoord is gevonden in bekende datalekken. Kies een ander wachtwoord.',
          );
        return;
      }
      confirming = true;
      await this.reset.confirm(this.code, password);
      if (version !== this.version) return;
      this.state.set('success');
      this.password.reset('', { emitEvent: false });
      this.policy.clearCache();
      await this.reset.signOut().catch(() => undefined);
      if (version === this.version) this.startRedirect();
    } catch (error) {
      if (version !== this.version) return;
      const code = (error as { code?: string }).code;
      if (
        code === 'auth/expired-action-code' ||
        code === 'auth/invalid-action-code' ||
        code === 'auth/user-disabled' ||
        code === 'auth/user-not-found'
      ) {
        this.state.set('error');
      }
      this.error.set(
        confirming
          ? this.errorMessage(error)
          : this.policy.getPasswordValidationErrorMessage(error),
      );
    } finally {
      this.saving.set(false);
    }
  }

  private errorMessage(error: unknown): string {
    switch ((error as { code?: string })?.code) {
      case 'auth/expired-action-code':
      case 'auth/action-code-expired':
        return this.mode() === 'verifyEmail'
          ? 'Deze verificatielink is verlopen. Vraag een nieuwe verificatiemail aan.'
          : 'Deze resetlink is verlopen. Vraag een nieuwe resetlink aan.';
      case 'auth/invalid-action-code':
      case 'auth/user-disabled':
      case 'auth/user-not-found':
        return this.mode() === 'verifyEmail'
          ? 'Deze verificatielink is ongeldig of al gebruikt. Vraag een nieuwe verificatiemail aan.'
          : 'Deze resetlink is ongeldig of al gebruikt. Vraag een nieuwe resetlink aan.';
      case 'auth/weak-password':
      case 'auth/password-does-not-meet-requirements':
        return 'Dit wachtwoord voldoet niet aan het wachtwoordbeleid. Kies een ander wachtwoord.';
      case 'auth/network-request-failed':
        return 'Er is een netwerkfout opgetreden. Controleer je verbinding en probeer het opnieuw.';
      default:
        return this.mode() === 'verifyEmail'
          ? 'Het e-mailadres kon niet worden geverifieerd. Probeer het opnieuw of vraag een nieuwe verificatiemail aan.'
          : 'Er is een fout opgetreden. Probeer het opnieuw of vraag een nieuwe resetlink aan.';
    }
  }

  private startRedirect() {
    if (this.continueUrl && !this.destroyRef.destroyed) {
      this.countdown.set(5);
      this.redirecting.set(true);
      this.redirectTimer = setInterval(() => {
        this.countdown.update((value) => value - 1);
        if (this.countdown() === 0) this.continue();
      }, 1000);
    }
  }

  continue() {
    clearInterval(this.redirectTimer);
    this.redirecting.set(false);
    if (!this.continueUrl) {
      void this.router.navigate(['/login']);
      return;
    }
    const url = new URL(this.continueUrl, window.location.origin);
    if (url.origin === window.location.origin && url.pathname === '/login') {
      void this.router.navigate(['/login']);
      return;
    }
    window.location.assign(url.href);
  }
}
